import ReactSelect, { FormatOptionLabelMeta, GroupBase, PropsValue, SelectComponentsConfig } from 'react-select';
import { customStyles } from './styles';
import { ReactNode } from 'react';
import { LoadingIndicator } from './ui/LoadingIndicator';
import { NoOptionsMessage } from './ui/NoOptionMessage';
import { SelectLabel } from './ui/SelectLabel';
import { InputError } from '../InputError/InputError';
import SelectedIcon from './images/SelectedIcon.svg?react';
import { Box } from '@/components/Box/Box';

export interface OptionType<T = string> {
  value: T;
  label: string;
  isDisabled?: boolean;
}

export type SelectType = {
  defaultOption?: OptionType;
  options: OptionType[];
  placeholder?: string;
  label?: ReactNode;
  value?: PropsValue<OptionType>;
  onChange?: (value: unknown) => void;
  onBlur?: (event) => void;
  error?: string;
  apiError?: string;
  isSupportError?: boolean;
  isSearchable?: boolean;
  isLoading?: boolean;
  postfixLabel?: ReactNode;
  disabled?: boolean;
  emptyOptionPlaceholder?: string;
  hasPortal?: boolean;
  variant?: 'standard' | 'compact';
  size?: 'medium' | 'small';
  isMulti?: boolean;
  closeMenuOnSelect?: boolean;
  hideSelectedOptions?: boolean;
  components?: SelectComponentsConfig<OptionType, boolean, GroupBase<OptionType>>;
  formatOptionLabel?: (option: OptionType, meta: FormatOptionLabelMeta<OptionType>) => ReactNode;
};

const defaultFormatOptionLabel = (option: OptionType, meta: FormatOptionLabelMeta<OptionType>) => {
  const isMenuItem = meta.context === 'value';
  const isSelected = !!meta?.selectValue.find((item) => option.value === item?.value);
  const hasIcon = isSelected && !isMenuItem;

  return (
    <Box display='flex' justifyContent='space-between'>
      <Box ellipsis>{option.label}</Box>
      {hasIcon && (
        <Box flexShrink={0}>
          <SelectedIcon />
        </Box>
      )}
    </Box>
  );
};

export const Select = ({
  placeholder,
  options,
  label,
  onChange,
  onBlur,
  error,
  apiError,
  isSupportError = true,
  isSearchable = false,
  isLoading,
  postfixLabel,
  disabled,
  emptyOptionPlaceholder = 'No options available',
  defaultOption,
  value,
  hasPortal = true,
  variant = 'standard',
  size = 'medium',
  isMulti = false,
  closeMenuOnSelect = true,
  hideSelectedOptions = false,
  components,
  formatOptionLabel = defaultFormatOptionLabel,
}: SelectType) => {
  return (
    <Box width='100%'>
      <SelectLabel label={label} postfixLabel={postfixLabel} />
      <ReactSelect
        value={value}
        defaultValue={defaultOption}
        menuPlacement='auto'
        minMenuHeight={400}
        styles={customStyles({ error, hasPortal, variant, size })}
        placeholder={placeholder}
        options={options}
        menuPortalTarget={hasPortal ? document.getElementById('select') : null}
        isSearchable={isSearchable}
        onChange={(newValue) => onChange(newValue)}
        onBlur={onBlur}
        isDisabled={disabled}
        isLoading={isLoading}
        formatOptionLabel={formatOptionLabel}
        noOptionsMessage={() => <NoOptionsMessage message={emptyOptionPlaceholder} />}
        components={{
          LoadingIndicator,
          ...components,
        }}
        isMulti={isMulti}
        closeMenuOnSelect={closeMenuOnSelect}
        hideSelectedOptions={hideSelectedOptions}
      />
      {isSupportError && <InputError error={error} apiError={apiError} />}
    </Box>
  );
};
